import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ThemeProvider as MuiThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import ScopedCssBaseline from "@mui/material/ScopedCssBaseline";
import { makeLightTheme } from "./lightTheme";
import { makeDarkTheme } from "./darkTheme";
import {
  ACCENT_VARS, accentForMode, CANVAS_BY_MODE, type ColorMode, defaultSelection,
  deriveBrandShades, type AppearanceSelection, isHexColor, type ResolvedMode,
  appearanceStorageKey, readStoredAppearance, resolveAccent, resolveMode,
  stampBrandMark, stampBrandSurfaces, writeStoredAppearance,
} from "./appearance";

export interface AppearanceContextValue {
  /** The user's color-mode choice (light / dark / system). */
  colorMode: ColorMode;
  /** The concrete mode after resolving "system" against the OS. */
  mode: ResolvedMode;
  /** The accent for the ACTIVE mode (accentLight in light, accentDark in dark).
   *  This is what the picker highlights and what recolors the UI right now. */
  accent: string;
  /** Both per-mode accents, exposed for Settings (snapshot / save / dirty). */
  accentLight: string;
  accentDark: string;
  setColorMode: (m: ColorMode) => void;
  /** Set the accent for the CURRENT mode only — the other mode is untouched. */
  setAccent: (hex: string) => void;
  /** Quick light↔dark switch (sets an explicit mode, leaving "system"). */
  toggleMode: () => void;
  /** Back to defaults: system mode + emerald (light) / blue (dark) accents. */
  reset: () => void;
  /** Set both per-mode accents at once, leaving the color mode. Used for server
   *  hydration and Settings revert. Either field may be omitted to keep the
   *  current local value. */
  setAccents: (accents: { accentLight?: string; accentDark?: string }) => void;
}

const AppearanceContext = createContext<AppearanceContextValue | null>(null);

export function useAppearance(): AppearanceContextValue {
  const ctx = useContext(AppearanceContext);
  if (!ctx) throw new Error("useAppearance must be used inside <AppearanceProvider>");
  return ctx;
}

function systemPrefersDark(): boolean {
  return typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;
}

/** Mirror the selection onto <html> so CSS picks it up app-wide. The same
 *  stamping runs pre-paint from the inline script in index.html — this keeps
 *  React state and the DOM in sync after hydration and on every change. */
function stamp(resolved: ResolvedMode, accentId: string, el: HTMLElement): void {
  el.setAttribute("data-mode", resolved);
  // Resolve the active mode's accent identity to its per-mode shade.
  const eff = resolveAccent(accentId, resolved);
  for (const v of ACCENT_VARS) el.style.setProperty(v, eff);
}

/** Initial selection. In the embed, the host's snippet sets the accent
 *  (`brandAccent`) and/or the colour mode (`brandMode`, light|dark) — applied
 *  only when the viewer has nothing stored *for this surface*. Ignored (unset)
 *  in the SPA.
 *
 *  "This surface" matters: the embed shares an origin (and so a localStorage)
 *  with the SPA, so it reads a brand-scoped key. Otherwise a viewer who had
 *  ever picked an accent in the SPA would see the embed ignore the host's brand
 *  entirely. */
function seedSelection(brandAccent?: string, brandMode?: ColorMode): AppearanceSelection {
  const key = appearanceStorageKey(brandAccent);
  const stored = readStoredAppearance(key);
  let untouched = false;
  try {
    untouched = localStorage.getItem(key) == null;
  } catch {
    /* private mode → treat as touched (don't override) */
  }
  if (!untouched) return stored;
  let sel = stored;
  if (brandAccent && isHexColor(brandAccent)) {
    const { light, dark } = deriveBrandShades(brandAccent);
    sel = { ...sel, accentLight: light, accentDark: dark };
  }
  // Only an explicit light/dark from the snippet overrides; "system"/junk = keep.
  if (brandMode === "light" || brandMode === "dark") {
    sel = { ...sel, mode: brandMode };
  }
  return sel;
}

export function AppearanceProvider({
  children,
  brandAccent,
  brandMode,
  brandBg,
  brandSidebar,
  brandText,
  portalContainer,
  themeRoot,
}: {
  children: React.ReactNode;
  /** Embed only (widget.js): where MUI portals menus, popovers, dialogs and
   *  tooltips. Default is document.body — outside the widget's shadow root,
   *  where its styles do not reach. */
  portalContainer?: HTMLElement;
  /** Embed only (widget.js): the element the mode/accent/brand variables are
   *  stamped on. Default <html>. Inside a shadow root the stylesheet's :host
   *  defaults beat anything inherited from <html> (the accent never applied),
   *  and stamping <html> would leak Artie's variables onto the host page. */
  themeRoot?: HTMLElement;
  /** Embed only: a company brand hex used as the DEFAULT accent (see
   *  seedSelection). Ignored in the SPA. */
  brandAccent?: string;
  /** Embed only: force the colour mode (light|dark) from the host snippet.
   *  Ignored in the SPA. */
  brandMode?: ColorMode;
  /** Embed only: page background colour (hex) from the host snippet. Applied
   *  verbatim (absolute) over the mode's token; ignored in the SPA. */
  brandBg?: string;
  /** Embed only: sidebar/nav background colour (hex) from the host snippet. */
  brandSidebar?: string;
  /** Embed only: primary text colour (hex) from the host snippet. Omit to
   *  auto-derive a readable ink from brandBg/brandSurface. */
  brandText?: string;
}) {
  const [sel, setSel] = useState<AppearanceSelection>(() => seedSelection(brandAccent, brandMode));
  // Track the OS preference so "system" mode resolves live.
  const [prefersDark, setPrefersDark] = useState<boolean>(() => systemPrefersDark());

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => setPrefersDark(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const resolved = resolveMode(sel.mode, prefersDark);
  // The accent that's actually in play right now (per-mode).
  const accentId = accentForMode(sel, resolved);

  // Skip persisting on the initial mount: an embed viewer who only ever sees the
  // company default must NOT get it pinned to localStorage (so a later brand
  // change is still adopted, and seedSelection's "untouched" check stays true).
  // The first explicit change persists as normal. Host-pushed brand changes set
  // this again for the same reason (see the brand-change effect below).
  const skipPersist = useRef(true);
  useEffect(() => {
    const root = themeRoot ?? document.documentElement;
    stamp(resolved, accentId, root);
    // Snippet-driven surface palette (embed only): absolute colours stamped
    // straight onto the CSS vars, so they win over the mode's token set and hold
    // across a mode toggle. NOT part of AppearanceSelection — never persisted.
    stampBrandSurfaces(
      root,
      { bg: brandBg, sidebar: brandSidebar, text: brandText },
      CANVAS_BY_MODE[resolved],
    );
    // The app marks track the host's raw brand, not the per-mode accent shade —
    // a mode toggle shouldn't repaint the logo.
    stampBrandMark(root, brandAccent, brandSidebar);
    if (skipPersist.current) {
      skipPersist.current = false;
      return;
    }
    writeStoredAppearance(sel, appearanceStorageKey(brandAccent));
  }, [resolved, accentId, sel, brandAccent, brandBg, brandSidebar, brandText]);

  // Embed only: the host re-declaring its brand AFTER mount (themeBridge's
  // artie:set-theme, sent when the host's own theme changes) is an explicit
  // decision, so unlike the mount-time seed it wins over a stored viewer tweak.
  // It stays out of localStorage though — persisting it would make this pushed
  // colour outrank whatever the host puts in the hash on the next load.
  const lastBrand = useRef({ accent: brandAccent, mode: brandMode });
  useEffect(() => {
    const accentChanged = brandAccent !== lastBrand.current.accent;
    const modeChanged = brandMode !== lastBrand.current.mode;
    lastBrand.current = { accent: brandAccent, mode: brandMode };
    if (!accentChanged && !modeChanged) return;

    const nextAccents =
      accentChanged && isHexColor(brandAccent) ? deriveBrandShades(brandAccent) : null;
    const nextMode =
      modeChanged && (brandMode === "light" || brandMode === "dark") ? brandMode : null;
    if (!nextAccents && !nextMode) return;

    skipPersist.current = true;
    setSel((prev) => ({
      ...prev,
      ...(nextAccents ? { accentLight: nextAccents.light, accentDark: nextAccents.dark } : {}),
      ...(nextMode ? { mode: nextMode } : {}),
    }));
  }, [brandAccent, brandMode]);

  const setColorMode = useCallback((m: ColorMode) => {
    setSel((prev) => ({ ...prev, mode: m }));
  }, []);

  // Editing an accent only touches the mode currently on screen, so light and
  // dark stay independent.
  const setAccent = useCallback((hex: string) => {
    setSel((prev) =>
      resolved === "dark" ? { ...prev, accentDark: hex } : { ...prev, accentLight: hex },
    );
  }, [resolved]);

  const toggleMode = useCallback(() => {
    setSel((prev) => ({
      ...prev,
      mode: resolveMode(prev.mode, systemPrefersDark()) === "dark" ? "light" : "dark",
    }));
  }, []);

  const reset = useCallback(() => {
    setSel(defaultSelection());
  }, []);

  const setAccents = useCallback(
    (a: { accentLight?: string; accentDark?: string }) => {
      setSel((prev) => ({
        ...prev,
        accentLight: a.accentLight ?? prev.accentLight,
        accentDark: a.accentDark ?? prev.accentDark,
      }));
    },
    [],
  );

  const value = useMemo<AppearanceContextValue>(
    () => ({
      colorMode: sel.mode, mode: resolved, accent: accentId,
      accentLight: sel.accentLight, accentDark: sel.accentDark,
      setColorMode, setAccent, toggleMode, reset, setAccents,
    }),
    [sel, resolved, accentId, setColorMode, setAccent, toggleMode, reset, setAccents],
  );

  // MUI bridge: unconverted pages (Projects/Skills/Knowledge/Plugins/Schedules/
  // Members) get a live MUI theme whose `primary` IS the active accent — a real
  // hex — so every MUI control (buttons, tabs, selected/hover states) follows
  // the same accent as the Tailwind side. Surfaces come from CSS vars, so one
  // light + one dark MUI theme cover everything. Removed once those pages
  // migrate off MUI (Phase 4/5).
  const muiTheme = useMemo(() => {
    // The active mode's accent, resolved to its per-mode shade.
    const primary = resolveAccent(accentId, resolved);
    const base = resolved === "dark" ? makeDarkTheme(primary) : makeLightTheme(primary);
    if (!portalContainer) return base;
    // Modal: Dialog/Drawer. Popover (and Menu, built on it) passes Modal an
    // explicit container (the anchor's document.body), so it needs its own
    // default. Popper: Tooltip/Autocomplete.
    return createTheme(base, {
      components: {
        MuiModal: { defaultProps: { container: portalContainer } },
        MuiPopover: { defaultProps: { container: portalContainer } },
        MuiPopper: { defaultProps: { container: portalContainer } },
      },
    });
  }, [resolved, accentId, portalContainer]);
  return (
    <AppearanceContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>
        {themeRoot ? (
          // widget.js: there is no <body> inside a shadow root for CssBaseline
          // to style, so the base font/colour/line-height go on this subtree.
          <ScopedCssBaseline sx={{ height: "100%", bgcolor: "transparent" }}>{children}</ScopedCssBaseline>
        ) : (
          <>
            <CssBaseline />
            {children}
          </>
        )}
      </MuiThemeProvider>
    </AppearanceContext.Provider>
  );
}
